/**
 * Utility functions for handling plan names and legacy "Old" plans
 */

/**
 * Normalize plan name by removing " Old" suffix
 * @param planName - The plan name (e.g., "Starter", "Starter Old")
 * @returns Base plan name without " Old" suffix
 */
export function normalizePlanName(planName: string | null | undefined): string {
  if (!planName) return '';
  return planName.replace(/ Old$/i, '').trim();
}

/**
 * Check if a plan is a legacy "Old" plan
 * @param planName - The plan name to check
 * @returns True if the plan name ends with " Old"
 */
export function isLegacyPlan(planName: string | null | undefined): boolean {
  if (!planName) return false;
  return planName.endsWith(' Old');
}

/**
 * Compare two plan names, ignoring " Old" suffix
 * @param planName1 - First plan name
 * @param planName2 - Second plan name
 * @returns True if both plans are the same (ignoring " Old" suffix)
 */
export function isSamePlan(planName1: string | null | undefined, planName2: string | null | undefined): boolean {
  return normalizePlanName(planName1) === normalizePlanName(planName2);
}

/**
 * Check if a plan is the base/starter plan
 * @param planName - The plan name to check
 * @returns True if it's Starter or Starter Old
 */
export function isStarterPlan(planName: string | null | undefined): boolean {
  return normalizePlanName(planName) === 'Starter';
}

/**
 * Get plan display name (shows "(Legacy)" badge for old plans)
 * @param planName - The plan name
 * @returns Display-friendly plan name
 */
export function getPlanDisplayName(planName: string | null | undefined): string {
  if (!planName) return 'No Plan';
  
  if (isLegacyPlan(planName)) {
    return `${normalizePlanName(planName)} (Legacy)`;
  }
  
  return planName;
}
